/**
 * Dialog Content Configurations
 * Defines the specific content for each document upload type
 */

import {
  Shield,
  FileText,
  Award,
  Building,
  FileCheck,
  AlertCircle,
  CreditCard
} from 'lucide-react';
import { FileTypeSlug } from '@/lib/services/files/file-types';
import { acceptedExtensionsFor } from '@/lib/constants/file-type-accept';
import type { UploadDialogContent } from './base-upload-dialog';
import type { DocumentUploadConfig } from '@/hooks/use-document-upload';

export interface DialogConfig {
  upload: DocumentUploadConfig;
  content: UploadDialogContent;
  filePreview?: {
    onlyCrucial?: boolean;
    excludeCrucial?: boolean;
  };
}

export const DIALOG_CONFIGS = {
  deathCertificate: {
    upload: {
      fileType: FileTypeSlug.DEATH_CERTIFICATE,
      maxFiles: 1
    },
    content: {
      title: 'Upload Death Certificate',
      description:
        'Please upload the official death certificate for this matter. This is a crucial document required to begin the probate process.',
      alert: {
        icon: <Shield className="h-4 w-4" />,
        message: (
          <>
            <strong>Important:</strong> The death certificate is a legally
            required document that cannot be removed once uploaded. It can only
            be replaced with an updated version if needed.
          </>
        )
      },
      dropZone: {
        icon: (
          <FileCheck className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        ),
        title: 'Drop death certificate here',
        subtitle: 'or click to browse for the death certificate',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG'
      },
      buttons: {
        upload: 'Upload Death Certificate',
        browse: 'Browse Files',
        uploadAndExtract: 'Upload & extract'
      }
    },
    filePreview: {
      onlyCrucial: true
    }
  } as DialogConfig,

  will: {
    upload: {
      fileType: FileTypeSlug.WILL,
      maxFiles: 1
    },
    content: {
      title: 'Upload Will Document',
      description:
        'Please upload the last will and testament. Ensure you have the most recent version if multiple wills exist.',
      alert: {
        icon: <FileText className="h-4 w-4" />,
        message: (
          <>
            <strong>Note:</strong> The will determines how the estate will be
            distributed. This document forms the legal basis for the probate
            process and can only be replaced, not removed.
          </>
        )
      },
      dropZone: {
        icon: (
          <FileText className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        ),
        title: 'Drop will document here',
        subtitle: 'or click to browse for the will',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG'
      },
      buttons: {
        upload: 'Upload Will',
        browse: 'Browse Files',
        uploadAndExtract: 'Upload & extract'
      }
    },
    filePreview: {
      onlyCrucial: true
    }
  } as DialogConfig,

  grantOfRepresentation: {
    upload: {
      fileType: FileTypeSlug.GRANT_OF_representation,
      maxFiles: 1
    },
    content: {
      title: 'Upload Grant of Representation',
      description:
        'Please upload the Grant of Probate or Letters of Administration issued by the court. This document provides legal authority to administer the estate.',
      alert: {
        icon: <Award className="h-4 w-4" />,
        message: (
          <>
            <strong>Important:</strong> The Grant of Representation is the
            court's official authorization to manage the deceased's estate.
            Ensure you upload the complete document with all pages and the court
            seal visible.
          </>
        )
      },
      dropZone: {
        icon: (
          <Award className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        ),
        title: 'Drop Grant of Representation here',
        subtitle: 'or click to browse for the grant document',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG'
      },
      buttons: {
        upload: 'Upload Grant',
        browse: 'Browse Files',
        uploadAndExtract: 'Upload & extract'
      }
    },
    filePreview: {
      excludeCrucial: true
    }
  } as DialogConfig,

  letterhead: {
    upload: {
      fileType: FileTypeSlug.LETTERHEAD,
      maxFiles: 1
    },
    content: {
      title: 'Upload Law Firm Letterhead',
      description:
        "Please upload your law firm's official letterhead template. This will be used for generating professional correspondence and official documents.",
      alert: {
        icon: <Building className="h-4 w-4" />,
        message: (
          <>
            <strong>Requirements:</strong> The letterhead should be a PDF
            containing your firm's logo, name, address and contact information.
            This template will be used across all matters for your firm.
          </>
        )
      },
      dropZone: {
        icon: (
          <Building className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        ),
        title: 'Drop letterhead template here',
        subtitle: 'or click to browse for your letterhead',
        // PDF only, which is what the backend's `letterhead` type accepts. This line read
        // "PDF, JPG, PNG, DOC, DOCX" until VALE-1150 — four formats a firm admin could pick
        // and then watch fail after the upload had already gone to S3.
        acceptedFormats: 'Accepted formats: PDF'
      },
      buttons: {
        upload: 'Upload Letterhead',
        browse: 'Browse Files'
      }
    },
    filePreview: {
      excludeCrucial: true
    }
  } as DialogConfig,

  additionalDocument: {
    upload: {
      fileType: FileTypeSlug.ADDITIONAL_DOCUMENT,
      maxFiles: 5
    },
    content: {
      title: 'Upload Additional Documents',
      description:
        'Add supporting documents to this matter. You can upload up to 5 files at once.',
      alert: {
        icon: <AlertCircle className="h-4 w-4" />,
        message:
          'You can upload various types of supporting documents including IDs, forms, correspondence, and other relevant files.'
      },
      dropZone: {
        icon: (
          <FileText className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
        ),
        title: 'Drop files here',
        subtitle: 'or click to browse for documents',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG, DOC, DOCX'
      },
      buttons: {
        upload: 'Upload Documents',
        browse: 'Browse Files'
      }
    }
  } as DialogConfig,

  primaryId: {
    upload: {
      fileType: FileTypeSlug.PRIMARY_ID,
      maxFiles: 1
    },
    content: {
      title: 'Upload Identification Document',
      description:
        "Please upload a valid form of identification for the Legal Personal Representative. This can be a driver's license, passport, or other government-issued ID.",
      alert: {
        icon: <CreditCard className="h-4 w-4" />,
        message: (
          <>
            <strong>Important:</strong> The ID document will be used to verify
            the identity of the Legal Personal Representative. Please ensure the
            document is current and clearly legible.
          </>
        )
      },
      dropZone: {
        icon: <Shield className="text-muted-foreground mb-4 h-10 w-10" />,
        title: 'Drop ID document here',
        subtitle: 'or click to browse',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG'
      },
      buttons: {
        upload: 'Upload ID',
        browse: 'Browse Files'
      }
    }
  } as DialogConfig,

  secondaryId: {
    upload: {
      fileType: FileTypeSlug.SECONDARY_ID,
      maxFiles: 1
    },
    content: {
      title: 'Upload Secondary Identification',
      description:
        'Please upload a secondary form of identification for additional verification. This can be a Medicare card, utility bill, or other supporting document.',
      alert: {
        icon: <CreditCard className="h-4 w-4" />,
        message: (
          <>
            <strong>Note:</strong> A secondary ID provides additional
            verification and may be required by certain institutions when
            processing estate matters.
          </>
        )
      },
      dropZone: {
        icon: <Shield className="text-muted-foreground mb-4 h-10 w-10" />,
        title: 'Drop secondary ID here',
        subtitle: 'or click to browse',
        acceptedFormats: 'Accepted formats: PDF, JPG, PNG'
      },
      buttons: {
        upload: 'Upload Secondary ID',
        browse: 'Browse Files'
      }
    }
  } as DialogConfig
};

/**
 * The extensions this dialog's drop zone accepts, derived from the backend file type it
 * uploads to (VALE-1150).
 *
 * It used to translate a hand-written `acceptedMimeTypes` list through a local MIME-to-extension
 * map — a third copy of a mapping that already existed in `file-type-accept.ts`, and one that
 * silently dropped any format missing from it. Both the list and the map are gone: the slug on
 * `config.upload.fileType` is the only thing a config now has to get right.
 */
export function getAcceptedFileTypes(config: DialogConfig): string[] {
  return acceptedExtensionsFor(config.upload.fileType);
}
